import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import {
  calcularPontosCiclo,
  calcularProgressao,
  calcularLimiarFuncionario,
  aplicaRegraEspecial18Anos,
  PONTOS_REGRA_18_ANOS,
  pontosParaAvaliacao,
  PONTOS_HISTORICO,
  type AvaliacaoSiadap,
  type PontosEscalasConfig,
} from '@/utils/progressaoUtils';

type ElegibilidadeFilter = 'todos' | 'elegiveis' | 'proximos' | 'em_progresso';
type ViewMode = 'lista' | 'unidade';

const avatarPalette = [
  { bg: 'bg-rose-100', text: 'text-rose-600' },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
  { bg: 'bg-teal-100', text: 'text-teal-600' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getAvatarPalette(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarPalette.length;
  return avatarPalette[isNaN(idx) ? 0 : idx];
}

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('');
}

function fmt(n: number) {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
}

const avalCfg: Record<string, { bg: string; text: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700'   },
  Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600'    },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700'  },
  Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700'    },
  Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600'    },
  Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700'    },
};

const statusCfgMap = {
  pronto:       { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', bar: 'bg-emerald-500', label: 'Elegível'     },
  proximo:      { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   bar: 'bg-amber-400',   label: 'Próximo'      },
  em_progresso: { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-300',    label: 'Em Progresso' },
} as const;

export default function RelatoriosPage() {
  const navigate = useNavigate();
  const { funcionarios, loading } = useFuncionarios();
  const { registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, loadingSiadap } = useSiadap();
  const { settings } = useAppSettings();
  const printRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [unidadeFilter, setUnidadeFilter] = useState('');
  const [elegibilidadeFilter, setElegibilidadeFilter] = useState<ElegibilidadeFilter>('todos');
  const [sortBy, setSortBy] = useState<'nome' | 'pontos' | 'nivel'>('nome');
  const [viewMode, setViewMode] = useState<ViewMode>('lista');
  const [expandedUnidades, setExpandedUnidades] = useState<Set<string>>(new Set());

  // Pesquisa de relatório individual
  const [relSearch, setRelSearch] = useState('');
  const [relOpen, setRelOpen] = useState(false);
  const relRef = useRef<HTMLDivElement>(null);
  const relInputRef = useRef<HTMLInputElement>(null);

  const relFiltrados = useMemo(() => {
    const q = relSearch.toLowerCase().trim();
    if (!q) return funcionarios.slice(0, 8);
    return funcionarios
      .filter((f) => f.nome.toLowerCase().includes(q) || f.id.includes(q))
      .slice(0, 8);
  }, [funcionarios, relSearch]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (relRef.current && !relRef.current.contains(e.target as Node)) {
        setRelOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Atalho Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        relInputRef.current?.focus();
        setRelOpen(true);
      }
      if (e.key === 'Escape') {
        setRelOpen(false);
        relInputRef.current?.blur();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isLoading = loading || loadingSiadap;

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente:   settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom:         settings.pontos_bom,
    Regular:     settings.pontos_regular,
    Inadequado:  settings.pontos_inadequado,
  }), [settings]);

  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente:   settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom:         settings.pontos_bom,
      Regular:     settings.pontos_regular,
      Inadequado:  settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente:   settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom:         settings.pontos_bom_2023,
      Regular:     settings.pontos_regular_2023,
      Inadequado:  settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente:   settings.pontos_excelente_pre2023,
      Relevante:   settings.pontos_relevante_pre2023,
      Adequado:    settings.pontos_adequado_pre2023,
      Inadequado:  settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom:         settings.pontos_adequado_pre2023,
      Regular:     settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const carreiras = useMemo(() => Array.from(new Set(funcionarios.map((f) => f.carreira))).sort(), [funcionarios]);
  const unidades = useMemo(() => Array.from(new Set(funcionarios.map((f) => f.unidadeOrganica).filter(Boolean))).sort() as string[], [funcionarios]);

  const dadosPorFuncionario = useMemo(() => {
    return funcionarios.map((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      const descontosProgressoes = pontosDescontadosPorFuncionario[f.id] ?? 0;
      const descontosManuais = f.pontosDescontados ?? 0;
      let pontosDescontados: number;
      if (descontosManuais > descontosProgressoes) {
        pontosDescontados = descontosManuais;
      } else if (descontosManuais === descontosProgressoes) {
        pontosDescontados = descontosProgressoes;
      } else {
        pontosDescontados = descontosProgressoes + descontosManuais;
      }
      const isPrepav = f.excecaoPrepav === true;
      const temRegistos = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
      // Pontos manuais são SEMPRE somados
      const pontosManuais = f.pontosAtuais ?? 0;

      const pontosTotal = calcularPontosCiclo(siadapDoFunc, historicoDoFunc, pontosDescontados, pontosManuais, pontosMap, undefined, escalas);
      const { limiar } = calcularLimiarFuncionario(siadapDoFunc, historicoDoFunc, settings.pontos_progressao || 8, settings.pontos_progressao_pre2025 || 10, settings.pontos_progressao_2023 || 8, escalas);
      const jaProgrediu = (pontosDescontadosPorFuncionario[f.id] ?? 0) > 0;
      const regraEspecial = aplicaRegraEspecial18Anos(f.dataEntradaFuncaoPublica, pontosTotal, jaProgrediu, 2024, limiar);
      const limiarEfetivo = regraEspecial ? PONTOS_REGRA_18_ANOS : limiar;
      const prog = calcularProgressao(pontosTotal, f.nivelAtual, limiarEfetivo);

      const pontosSiadap = siadapDoFunc.reduce((s, r) => s + pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas), 0);
      const pontosHistorico = historicoDoFunc.reduce((s, r) => s + (PONTOS_HISTORICO[r.classificacaoServico] ?? 0), 0);

      const ultimaAvaliacao = [...siadapDoFunc].sort((a, b) => {
        const anoA = parseInt((a.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
        const anoB = parseInt((b.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
        return anoB - anoA;
      })[0];

      return {
        funcionario: f,
        pontosTotal,
        pontosDescontados,
        pontosSiadap,
        pontosHistorico,
        limiarEfetivo,
        regraEspecial,
        status: prog.status as 'pronto' | 'proximo' | 'em_progresso',
        percentagem: prog.percentagem,
        totalAvaliacoes: siadapDoFunc.length + historicoDoFunc.length,
        ultimaAvaliacao: ultimaAvaliacao?.avaliacao ?? null,
        ultimoPeriodo: ultimaAvaliacao?.periodoAvaliacao ?? null,
      };
    });
  }, [funcionarios, registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, pontosMap, escalas, settings]);

  const filtered = useMemo(() => {
    let list = dadosPorFuncionario.filter((d) => {
      const f = d.funcionario;
      const matchSearch = search === '' || f.nome.toLowerCase().includes(search.toLowerCase()) || f.id.includes(search);
      const matchCarreira = carreiraFilter === '' || f.carreira === carreiraFilter;
      const matchUnidade = unidadeFilter === '' || f.unidadeOrganica === unidadeFilter;
      const matchEleg =
        elegibilidadeFilter === 'todos' ||
        (elegibilidadeFilter === 'elegiveis' && d.status === 'pronto') ||
        (elegibilidadeFilter === 'proximos' && d.status === 'proximo') ||
        (elegibilidadeFilter === 'em_progresso' && d.status === 'em_progresso');
      return matchSearch && matchCarreira && matchUnidade && matchEleg;
    });
    list = [...list].sort((a, b) => {
      if (sortBy === 'pontos') return b.pontosTotal - a.pontosTotal;
      if (sortBy === 'nivel') return b.funcionario.nivelAtual - a.funcionario.nivelAtual;
      return a.funcionario.nome.localeCompare(b.funcionario.nome);
    });
    return list;
  }, [dadosPorFuncionario, search, carreiraFilter, unidadeFilter, elegibilidadeFilter, sortBy]);

  // Agrupamento por unidade orgânica
  const porUnidade = useMemo(() => {
    const grupos: Record<string, typeof filtered> = {};
    filtered.forEach((d) => {
      const key = d.funcionario.unidadeOrganica || '(Sem Unidade)';
      if (!grupos[key]) grupos[key] = [];
      grupos[key].push(d);
    });
    return Object.entries(grupos).sort(([, a], [, b]) => {
      const eleA = a.filter((d) => d.status === 'pronto').length;
      const eleB = b.filter((d) => d.status === 'pronto').length;
      if (eleB !== eleA) return eleB - eleA;
      return a[0].funcionario.unidadeOrganica?.localeCompare(b[0].funcionario.unidadeOrganica ?? '') ?? 0;
    });
  }, [filtered]);

  const stats = useMemo(() => ({
    total: dadosPorFuncionario.length,
    elegiveis: dadosPorFuncionario.filter((d) => d.status === 'pronto').length,
    proximos: dadosPorFuncionario.filter((d) => d.status === 'proximo').length,
    emProgresso: dadosPorFuncionario.filter((d) => d.status === 'em_progresso').length,
  }), [dadosPorFuncionario]);

  const elegibilidadeOptions: { key: ElegibilidadeFilter; label: string; count: number; color: string; activeBg: string; activeText: string }[] = [
    { key: 'todos',        label: 'Todos',        count: stats.total,        color: 'text-zinc-500',    activeBg: 'bg-zinc-900',    activeText: 'text-white' },
    { key: 'elegiveis',    label: 'Elegíveis',    count: stats.elegiveis,    color: 'text-emerald-600', activeBg: 'bg-emerald-600', activeText: 'text-white' },
    { key: 'proximos',     label: 'Próximos',     count: stats.proximos,     color: 'text-amber-600',   activeBg: 'bg-amber-500',   activeText: 'text-white' },
    { key: 'em_progresso', label: 'Em Progresso', count: stats.emProgresso,  color: 'text-zinc-400',    activeBg: 'bg-zinc-500',    activeText: 'text-white' },
  ];

  function toggleUnidade(u: string) {
    setExpandedUnidades((prev) => {
      const next = new Set(prev);
      if (next.has(u)) next.delete(u);
      else next.add(u);
      return next;
    });
  }

  function expandAll() {
    setExpandedUnidades(new Set(porUnidade.map(([u]) => u)));
  }

  function collapseAll() {
    setExpandedUnidades(new Set());
  }

  function handleExportPdf() {
    window.print();
  }

  function handleExportUnidadePdf(unidade: string, funcs: typeof filtered) {
    // Cria uma janela de impressão com apenas os dados desta unidade
    const elegiveis = funcs.filter((d) => d.status === 'pronto').length;
    const proximos = funcs.filter((d) => d.status === 'proximo').length;
    const emProgresso = funcs.filter((d) => d.status === 'em_progresso').length;
    const mediaPontos = funcs.length > 0 ? funcs.reduce((s, d) => s + d.pontosTotal, 0) / funcs.length : 0;

    const linhas = funcs.map((d) => {
      const f = d.funcionario;
      const sc = statusCfgMap[d.status];
      const aval = d.ultimaAvaliacao ?? '—';
      return `
        <tr style="border-bottom:1px solid #f1f5f9;">
          <td style="padding:8px 12px;font-size:12px;font-weight:600;color:#1e293b;">${f.nome}</td>
          <td style="padding:8px 12px;font-size:12px;color:#64748b;">${f.carreira}</td>
          <td style="padding:8px 12px;font-size:12px;color:#64748b;">Nível ${f.nivelAtual}</td>
          <td style="padding:8px 12px;font-size:12px;color:#64748b;">${aval}</td>
          <td style="padding:8px 12px;font-size:12px;font-weight:700;color:#1e293b;">${fmt(d.pontosTotal)} / ${d.limiarEfetivo}</td>
          <td style="padding:8px 12px;font-size:12px;text-align:center;">
            <span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:999px;background:${d.status === 'pronto' ? '#dcfce7' : d.status === 'proximo' ? '#fef3c7' : '#f1f5f9'};color:${d.status === 'pronto' ? '#166534' : d.status === 'proximo' ? '#92400e' : '#64748b'};">${sc.label}</span>
          </td>
        </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="pt"><head><meta charset="UTF-8"/><title>Relatório - ${unidade}</title>
    <style>body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:20px;background:#fff;}table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;}th{background:#f8fafc;padding:8px 12px;text-align:left;font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.05em;border-bottom:1px solid #e2e8f0;}@page{margin:1cm;size:A4;}</style>
    </head><body>
    <div style="margin-bottom:20px;">
      <p style="margin:0;font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.08em;">${settings.nome_camara} · SIADAP para RH</p>
      <h1 style="margin:4px 0;font-size:20px;font-weight:700;color:#1e293b;">${unidade}</h1>
      <p style="margin:0;font-size:12px;color:#64748b;">Gerado em ${new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
    </div>
    <div style="display:flex;gap:16px;margin-bottom:20px;flex-wrap:wrap;">
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px;min-width:100px;">
        <p style="margin:0;font-size:10px;color:#94a3b8;text-transform:uppercase;">Total</p>
        <p style="margin:4px 0 0;font-size:22px;font-weight:700;color:#1e293b;">${funcs.length}</p>
      </div>
      <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px 16px;min-width:100px;">
        <p style="margin:0;font-size:10px;color:#16a34a;text-transform:uppercase;">Elegíveis</p>
        <p style="margin:4px 0 0;font-size:22px;font-weight:700;color:#166534;">${elegiveis}</p>
      </div>
      <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:12px 16px;min-width:100px;">
        <p style="margin:0;font-size:10px;color:#d97706;text-transform:uppercase;">Próximos</p>
        <p style="margin:4px 0 0;font-size:22px;font-weight:700;color:#92400e;">${proximos}</p>
      </div>
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px;min-width:100px;">
        <p style="margin:0;font-size:10px;color:#94a3b8;text-transform:uppercase;">Em Progresso</p>
        <p style="margin:4px 0 0;font-size:22px;font-weight:700;color:#64748b;">${emProgresso}</p>
      </div>
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px;min-width:100px;">
        <p style="margin:0;font-size:10px;color:#94a3b8;text-transform:uppercase;">Média Pontos</p>
        <p style="margin:4px 0 0;font-size:22px;font-weight:700;color:#1e293b;">${fmt(mediaPontos)}</p>
      </div>
    </div>
    <table>
      <thead><tr>
        <th>Nome</th><th>Carreira</th><th>Nível</th><th>Última Aval.</th><th>Pontos</th><th style="text-align:center;">Estado</th>
      </tr></thead>
      <tbody>${linhas}</tbody>
    </table>
    <p style="margin-top:20px;font-size:10px;color:#94a3b8;">${settings.nome_camara} · SIADAP para RH · Documento gerado automaticamente</p>
    </body></html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 500);
    }
  }

  const hasFilters = search || carreiraFilter || unidadeFilter || elegibilidadeFilter !== 'todos';

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-area { box-shadow: none !important; }
        }
      `}</style>

      <main className="flex-1 bg-slate-50/60">
        {/* PAGE HEADER */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5 no-print">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-file-chart-2-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Análise</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Relatórios</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Histórico completo por funcionário — SIADAP, pontos e progressões</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Pesquisa de relatório individual */}
              <div className="relative" ref={relRef}>
                <div className="flex items-center gap-2 border border-zinc-200 bg-white rounded-lg px-3 py-2 min-w-[280px] cursor-text focus-within:border-zinc-400 transition-colors"
                  onClick={() => { setRelOpen(true); relInputRef.current?.focus(); }}>
                  <div className="w-4 h-4 flex items-center justify-center text-zinc-400 flex-shrink-0">
                    <i className="ri-search-line text-sm" />
                  </div>
                  <input
                    ref={relInputRef}
                    type="text"
                    value={relSearch}
                    onChange={(e) => { setRelSearch(e.target.value); setRelOpen(true); }}
                    onFocus={() => setRelOpen(true)}
                    placeholder="Relatório individual..."
                    className="flex-1 text-[13px] text-zinc-700 placeholder-zinc-400 bg-transparent focus:outline-none"
                  />
                  {relSearch ? (
                    <button onClick={(e) => { e.stopPropagation(); setRelSearch(''); setRelOpen(false); }}
                      className="w-4 h-4 flex items-center justify-center text-zinc-400 hover:text-zinc-700 cursor-pointer flex-shrink-0">
                      <i className="ri-close-line text-xs" />
                    </button>
                  ) : (
                    <kbd className="hidden sm:flex items-center gap-0.5 text-[10px] font-semibold text-zinc-400 bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5 flex-shrink-0">
                      Ctrl+K
                    </kbd>
                  )}
                </div>

                {relOpen && relFiltrados.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-xl overflow-hidden z-50">
                    {relFiltrados.map((f) => {
                      const palette = getAvatarPalette(f.id);
                      const initials = getInitials(f.nome);
                      return (
                        <button
                          key={f.id}
                          onClick={() => { setRelOpen(false); setRelSearch(''); navigate(`/funcionarios/${f.id}/relatorio`); }}
                          className="whitespace-nowrap w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors cursor-pointer border-b border-zinc-50 last:border-0"
                        >
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                            <p className="text-[10px] text-zinc-400">{f.carreira}</p>
                          </div>
                          <div className="w-6 h-6 flex items-center justify-center text-zinc-300 flex-shrink-0">
                            <i className="ri-arrow-right-line text-sm" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {!isLoading && filtered.length > 0 && (
                <button
                  onClick={handleExportPdf}
                  className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-sm" /></div>
                  Exportar PDF
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">

          {/* STATS */}
          {!isLoading && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 print-area">
              {[
                { icon: 'ri-team-line',           value: stats.total,        label: 'Total de Funcionários',      iconBg: 'bg-zinc-100 text-zinc-600'    },
                { icon: 'ri-arrow-up-circle-line', value: stats.elegiveis,    label: 'Elegíveis para Progressão',  iconBg: 'bg-emerald-50 text-emerald-600' },
                { icon: 'ri-time-line',            value: stats.proximos,     label: 'Próximos da Elegibilidade',  iconBg: 'bg-amber-50 text-amber-600'   },
                { icon: 'ri-loader-line',          value: stats.emProgresso,  label: 'Em Progresso',               iconBg: 'bg-zinc-50 text-zinc-400'     },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${s.iconBg}`}>
                    <i className={`${s.icon} text-lg`} />
                  </div>
                  <div>
                    <p className="text-[22px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                    <p className="text-[11px] font-medium text-zinc-500 mt-0.5">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* RELATÓRIOS ESPECIALIZADOS */}
          {!isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 no-print">
              <button
                onClick={() => navigate('/relatorios/categoria')}
                className="whitespace-nowrap bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4 hover:border-zinc-300 transition-colors cursor-pointer text-left"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
                  <i className="ri-folder-chart-line text-amber-600 text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-zinc-800">Relatório por Carreira</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Distribuição de funcionários e elegibilidade por carreira profissional</p>
                </div>
                <div className="w-6 h-6 flex items-center justify-center text-zinc-300 flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-lg" />
                </div>
              </button>
              <button
                onClick={() => navigate('/relatorios/genero')}
                className="whitespace-nowrap bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4 hover:border-zinc-300 transition-colors cursor-pointer text-left"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50 flex-shrink-0">
                  <i className="ri-team-line text-teal-600 text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-zinc-800">Relatório por Género</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Análise de funcionários e elegibilidade por Masculino / Feminino</p>
                </div>
                <div className="w-6 h-6 flex items-center justify-center text-zinc-300 flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-lg" />
                </div>
              </button>
            </div>
          )}

          {/* FILTROS */}
          <div className="bg-white rounded-xl border border-zinc-100 p-4 space-y-3 no-print">
            <div className="flex gap-2.5 items-center">
              <div className="relative flex-1">
                <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                  <i className="ri-search-line text-sm" />
                </div>
                <input
                  type="text"
                  placeholder="Pesquisar por nome ou nº de funcionário..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                />
              </div>
              {/* Toggle de vista */}
              <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1 flex-shrink-0">
                <button
                  onClick={() => setViewMode('lista')}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-all cursor-pointer ${viewMode === 'lista' ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                  <i className="ri-list-check text-sm" />Lista
                </button>
                <button
                  onClick={() => setViewMode('unidade')}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-all cursor-pointer ${viewMode === 'unidade' ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                  <i className="ri-building-line text-sm" />Por Unidade
                </button>
              </div>
            </div>

            {/* Filtros de elegibilidade */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-zinc-400 flex-shrink-0">Estado:</span>
              {elegibilidadeOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setElegibilidadeFilter(opt.key)}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                    elegibilidadeFilter === opt.key ? `${opt.activeBg} ${opt.activeText}` : `bg-zinc-100 ${opt.color} hover:bg-zinc-200`
                  }`}
                >
                  {opt.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${elegibilidadeFilter === opt.key ? 'bg-white/20 text-white' : 'bg-white text-zinc-500'}`}>
                    {opt.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Filtros secundários */}
            <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-zinc-100">
              <select value={carreiraFilter} onChange={(e) => setCarreiraFilter(e.target.value)}
                className={`text-[13px] border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer ${carreiraFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}>
                <option value="">Todas as Carreiras</option>
                {carreiras.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={unidadeFilter} onChange={(e) => setUnidadeFilter(e.target.value)}
                className={`text-[13px] border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer ${unidadeFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}>
                <option value="">Todas as Unidades</option>
                {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-[11px] text-zinc-400 mr-1">Ordenar:</span>
                {([{ key: 'nome', label: 'Nome' }, { key: 'pontos', label: 'Pontos' }, { key: 'nivel', label: 'Nível' }] as { key: typeof sortBy; label: string }[]).map((s) => (
                  <button key={s.key} onClick={() => setSortBy(s.key)}
                    className={`whitespace-nowrap text-[12px] px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${sortBy === s.key ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[12px] text-zinc-400">
                <span className="font-semibold text-zinc-600">{filtered.length}</span> funcionário(s)
                {viewMode === 'unidade' && ` · ${porUnidade.length} unidade(s)`}
              </span>
              {hasFilters && (
                <button onClick={() => { setSearch(''); setCarreiraFilter(''); setUnidadeFilter(''); setElegibilidadeFilter('todos'); }}
                  className="whitespace-nowrap flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer">
                  <i className="ri-close-circle-line text-sm" />Limpar filtros
                </button>
              )}
            </div>
          </div>

          {/* CONTEÚDO */}
          {isLoading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" /></div>
              <p className="text-[13px] text-zinc-400">A carregar relatórios...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center"><i className="ri-search-line text-4xl text-zinc-200" /></div>
              <p className="text-[13px] font-medium text-zinc-400">Nenhum funcionário encontrado</p>
            </div>
          ) : viewMode === 'lista' ? (
            /* ── VISTA LISTA ── */
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden print-area" ref={printRef}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Funcionário</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Carreira / Unidade</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Última Avaliação</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontos</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide w-44">Progressão</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                    <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide no-print">Relatório</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d, i) => {
                    const f = d.funcionario;
                    const palette = getAvatarPalette(f.id);
                    const initials = getInitials(f.nome);
                    const sc = statusCfgMap[d.status];
                    const aval = d.ultimaAvaliacao ? (avalCfg[d.ultimaAvaliacao] ?? avalCfg['Adequado']) : null;
                    return (
                      <tr key={f.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors cursor-pointer group ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}
                        onClick={() => navigate(`/funcionarios/${f.id}/relatorio`)}>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>{initials}</div>
                            <div>
                              <p className="text-[13px] font-semibold text-zinc-800">{f.nome}</p>
                              <p className="text-[11px] text-zinc-400 font-mono">#{f.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-[12px] font-medium text-zinc-700">{f.carreira}</p>
                          <p className="text-[11px] text-zinc-400 truncate max-w-[160px]">{f.unidadeOrganica || '—'}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          {d.ultimaAvaliacao && aval ? (
                            <div>
                              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${aval.bg} ${aval.text}`}>{d.ultimaAvaliacao}</span>
                              {d.ultimoPeriodo && <p className="text-[10px] text-zinc-400 mt-0.5">{d.ultimoPeriodo}</p>}
                            </div>
                          ) : (
                            <span className="text-[12px] text-zinc-300 italic">Sem avaliações</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="text-[14px] font-bold text-zinc-800">{fmt(d.pontosTotal)}</span>
                            <span className="text-[11px] text-zinc-400">/ {d.limiarEfetivo}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] text-zinc-400">SIADAP: {fmt(d.pontosSiadap)}</span>
                            {d.pontosHistorico > 0 && <span className="text-[10px] text-zinc-300">· Hist: {fmt(d.pontosHistorico)}</span>}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="space-y-1">
                            <span className={`text-[11px] font-medium ${sc.text}`}>{d.percentagem.toFixed(0)}%</span>
                            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden w-36">
                              <div className={`h-full rounded-full ${sc.bar}`} style={{ width: `${d.percentagem}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                            <span className={`text-[12px] font-semibold ${sc.text}`}>{sc.label}</span>
                            {d.regraEspecial && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">18 anos</span>}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-right no-print">
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/funcionarios/${f.id}/relatorio`); }}
                            className="whitespace-nowrap inline-flex items-center gap-1.5 text-[12px] font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-900 hover:text-white px-3 py-1.5 rounded-lg transition-all cursor-pointer">
                            <i className="ri-file-chart-line text-sm" />Ver Relatório
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* ── VISTA POR UNIDADE ORGÂNICA ── */
            <div className="space-y-3 print-area" ref={printRef}>
              {/* Controles de expandir/recolher */}
              <div className="flex items-center justify-end gap-2 no-print">
                <button onClick={expandAll} className="whitespace-nowrap text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer flex items-center gap-1">
                  <i className="ri-expand-diagonal-line text-sm" />Expandir tudo
                </button>
                <span className="text-zinc-200">·</span>
                <button onClick={collapseAll} className="whitespace-nowrap text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer flex items-center gap-1">
                  <i className="ri-collapse-diagonal-line text-sm" />Recolher tudo
                </button>
              </div>

              {porUnidade.map(([unidade, funcs]) => {
                const isExpanded = expandedUnidades.has(unidade);
                const elegiveis = funcs.filter((d) => d.status === 'pronto').length;
                const proximos = funcs.filter((d) => d.status === 'proximo').length;
                const emProgresso = funcs.filter((d) => d.status === 'em_progresso').length;
                const mediaPontos = funcs.length > 0 ? funcs.reduce((s, d) => s + d.pontosTotal, 0) / funcs.length : 0;
                const pctElegiveis = funcs.length > 0 ? (elegiveis / funcs.length) * 100 : 0;
                const pctProximos = funcs.length > 0 ? (proximos / funcs.length) * 100 : 0;
                const pctProgresso = funcs.length > 0 ? (emProgresso / funcs.length) * 100 : 0;

                return (
                  <div key={unidade} className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                    {/* Header da unidade */}
                    <button
                      onClick={() => toggleUnidade(unidade)}
                      className="whitespace-nowrap w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-zinc-50/60 transition-colors cursor-pointer no-print"
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
                        <i className="ri-building-line text-zinc-600 text-base" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-[14px] font-bold text-zinc-800 truncate">{unidade}</h3>
                          <span className="text-[11px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-semibold">{funcs.length} func.</span>
                          {elegiveis > 0 && (
                            <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                              {elegiveis} elegíve{elegiveis === 1 ? 'l' : 'is'}
                            </span>
                          )}
                        </div>
                        {/* Barra de distribuição */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden flex">
                            {pctElegiveis > 0 && <div className="h-full bg-emerald-500" style={{ width: `${pctElegiveis}%` }} />}
                            {pctProximos > 0 && <div className="h-full bg-amber-400" style={{ width: `${pctProximos}%` }} />}
                            {pctProgresso > 0 && <div className="h-full bg-zinc-200" style={{ width: `${pctProgresso}%` }} />}
                          </div>
                          <span className="text-[10px] text-zinc-400 flex-shrink-0">Média: <strong className="text-zinc-600">{fmt(mediaPontos)} pts</strong></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-2 text-[11px]">
                          {elegiveis > 0 && <span className="text-emerald-700 font-semibold">{elegiveis} ✓</span>}
                          {proximos > 0 && <span className="text-amber-600">{proximos} ~</span>}
                          <span className="text-zinc-400">{emProgresso} ···</span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExportUnidadePdf(unidade, funcs); }}
                          className="whitespace-nowrap flex items-center gap-1 text-[11px] font-semibold text-zinc-500 bg-zinc-100 hover:bg-zinc-900 hover:text-white px-2.5 py-1.5 rounded-lg transition-all cursor-pointer"
                          title={`Exportar PDF de ${unidade}`}
                        >
                          <i className="ri-file-pdf-line text-sm" />
                          PDF
                        </button>
                        <div className="w-6 h-6 flex items-center justify-center text-zinc-400">
                          <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
                        </div>
                      </div>
                    </button>

                    {/* Cabeçalho para impressão (sempre visível no print) */}
                    <div className="hidden print:block px-5 py-3 border-b border-zinc-100">
                      <div className="flex items-center justify-between">
                        <h3 className="text-[14px] font-bold text-zinc-800">{unidade}</h3>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span>{funcs.length} funcionários</span>
                          <span className="text-emerald-700 font-semibold">{elegiveis} elegíveis</span>
                          <span>Média: {fmt(mediaPontos)} pts</span>
                        </div>
                      </div>
                    </div>

                    {/* Tabela de funcionários */}
                    {(isExpanded) && (
                      <div className="border-t border-zinc-100">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-zinc-50/80 border-b border-zinc-100">
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Funcionário</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Carreira</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Última Aval.</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Pontos</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide w-36">Progressão</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                              <th className="px-5 py-2.5 text-right text-[10px] font-semibold text-zinc-400 uppercase tracking-wide no-print">Ação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {funcs.map((d, i) => {
                              const f = d.funcionario;
                              const palette = getAvatarPalette(f.id);
                              const initials = getInitials(f.nome);
                              const sc = statusCfgMap[d.status];
                              const aval = d.ultimaAvaliacao ? (avalCfg[d.ultimaAvaliacao] ?? avalCfg['Adequado']) : null;
                              return (
                                <tr key={f.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors cursor-pointer group ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}
                                  onClick={() => navigate(`/funcionarios/${f.id}/relatorio`)}>
                                  <td className="px-5 py-3">
                                    <div className="flex items-center gap-2.5">
                                      <div className={`w-8 h-8 flex items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>{initials}</div>
                                      <div>
                                        <p className="text-[12px] font-semibold text-zinc-800">{f.nome}</p>
                                        <p className="text-[10px] text-zinc-400 font-mono">#{f.id}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-5 py-3">
                                    <p className="text-[11px] font-medium text-zinc-700">{f.carreira}</p>
                                    <p className="text-[10px] text-zinc-400">Nível {f.nivelAtual}</p>
                                  </td>
                                  <td className="px-5 py-3">
                                    {d.ultimaAvaliacao && aval ? (
                                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${aval.bg} ${aval.text}`}>{d.ultimaAvaliacao}</span>
                                    ) : (
                                      <span className="text-[11px] text-zinc-300 italic">—</span>
                                    )}
                                  </td>
                                  <td className="px-5 py-3">
                                    <span className="text-[13px] font-bold text-zinc-800">{fmt(d.pontosTotal)}</span>
                                    <span className="text-[10px] text-zinc-400 ml-1">/ {d.limiarEfetivo}</span>
                                  </td>
                                  <td className="px-5 py-3">
                                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden w-28">
                                      <div className={`h-full rounded-full ${sc.bar}`} style={{ width: `${d.percentagem}%` }} />
                                    </div>
                                    <span className={`text-[10px] font-medium ${sc.text} mt-0.5 block`}>{d.percentagem.toFixed(0)}%</span>
                                  </td>
                                  <td className="px-5 py-3">
                                    <div className="flex items-center gap-1">
                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                                      <span className={`text-[11px] font-semibold ${sc.text}`}>{sc.label}</span>
                                      {d.regraEspecial && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded-full font-bold ml-1">18a</span>}
                                    </div>
                                  </td>
                                  <td className="px-5 py-3 text-right no-print">
                                    <button onClick={(e) => { e.stopPropagation(); navigate(`/funcionarios/${f.id}/relatorio`); }}
                                      className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer ml-auto">
                                      <i className="ri-file-chart-line text-sm" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          {/* Rodapé da unidade com totais */}
                          <tfoot>
                            <tr className="bg-zinc-50 border-t border-zinc-100">
                              <td colSpan={3} className="px-5 py-2.5 text-[11px] text-zinc-400 font-medium">
                                {funcs.length} funcionário(s) · Média {fmt(mediaPontos)} pts
                              </td>
                              <td className="px-5 py-2.5">
                                <span className="text-[11px] font-semibold text-zinc-600">{fmt(funcs.reduce((s, d) => s + d.pontosTotal, 0))} pts total</span>
                              </td>
                              <td colSpan={3} className="px-5 py-2.5">
                                <div className="flex items-center gap-3 text-[11px]">
                                  {elegiveis > 0 && <span className="text-emerald-700 font-semibold">{elegiveis} elegíveis</span>}
                                  {proximos > 0 && <span className="text-amber-600">{proximos} próximos</span>}
                                  <span className="text-zinc-400">{emProgresso} em progresso</span>
                                </div>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="flex items-center justify-between text-[11px] text-zinc-400 pb-2 no-print">
              <span>SIADAP para RH · Câmara Municipal</span>
              <span>Atualizado em {new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
